/**
 * Shared activity list (t122u): avatar, a plain sentence from lib/activityFormat, then the relative time in its own
 * muted column with the exact date and time on hover. Used by the task panel's Activity tab and the building page.
 */
import type { ReactNode } from 'react';
import type { ActivityRefs } from '@tradeoutlet/shared';
import { Avatar } from '@/components/ui';
import { RelativeTime } from '@/components/time/RelativeTime';
import { groupActivity, type ActivityEntry, type ActivityRow } from '@/lib/activityFormat';

export function ActivitySentence({ e, onComment }: { e: ActivityEntry; onComment?: (id: string) => void }) {
  return (
    <>
      {e.phrases.map((p, i) => (
        <span key={i}>
          {i > 0 && (i === e.phrases.length - 1 ? ' and ' : ', ')}
          {p.text}
          {p.quote && <>{' '}{onComment && p.comment_id
            ? <button type="button" className="text-left italic text-slate-700 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-600 dark:text-slate-200" onClick={() => onComment(p.comment_id!)}>‘{p.quote}’</button>
            : <span className="italic text-slate-700 dark:text-slate-200">‘{p.quote}’</span>}</>}
        </span>
      ))}
    </>
  );
}

export function ActivityFeed({ rows, refs, nameOf, onComment, context, empty = 'Nothing yet.' }: {
  rows: ActivityRow[]; refs?: ActivityRefs; nameOf?: (id: string) => string | undefined;
  onComment?: (commentId: string) => void;
  /** Extra line under the sentence, e.g. which task (building and project feeds). */
  context?: (e: ActivityEntry) => ReactNode;
  empty?: string;
}) {
  const entries = groupActivity(rows, refs);
  if (!entries.length) return <p className="py-3 text-sm text-slate-500">{empty}</p>;
  const who = (id: string | null) => (id ? refs?.users[id] ?? nameOf?.(id) ?? 'Someone' : 'The Hub');
  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="activity-feed">
      {entries.map(e => (
        <li key={e.key} className="grid grid-cols-[28px_1fr_auto] items-start gap-x-3 py-2.5 text-sm" data-testid="activity-entry">
          <Avatar size={26} name={who(e.actor_id)} id={e.actor_id ?? e.key} />
          <div className="min-w-0 leading-snug">
            <span className="font-medium text-slate-900 dark:text-white">{who(e.actor_id)}</span>{' '}
            <span className="text-slate-700 dark:text-slate-300"><ActivitySentence e={e} onComment={onComment} /></span>
            {context && <div className="mt-0.5 truncate text-xs text-slate-500">{context(e)}</div>}
          </div>
          <RelativeTime iso={e.at} className="whitespace-nowrap pl-2 pt-0.5 text-xs text-slate-500 dark:text-slate-400" />
        </li>
      ))}
    </ul>
  );
}
